import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';

// Lo que tiene prestado, todo junto.
//
// «¿Hay alguna manera de que Gabriel pueda ver desde una fecha todo el dinero
// que tiene prestado?»
//
// ── DOS PREGUNTAS, NO UNA ──
//
// Antes esto vivía dentro de Fincas como una lista, y contestaba a medias. Hay
// dos preguntas distintas y confundirlas es el error clásico del que presta:
//
//   · LO QUE LE DEBEN HOY — la cartera. No tiene fechas: es lo que hay en la
//     calle ahora mismo.
//   · LO QUE PRESTÓ ENTRE DOS FECHAS — el flujo. Cuánto salió y cuánto volvió.
//
// Sumar lo prestado en el año creyendo que es lo que le deben da una cifra
// mucho más alta que la verdadera, y es con la que se toman decisiones malas.
//
// Por eso la cartera va arriba y sola, y el rango de fechas va aparte y
// rotulado como otra cosa.

const mes = (d) => Math.floor(d / 30);

// Cuánto lleva esperando, dicho como se dice.
const desdeCuando = (dias) => {
  if (dias < 1) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  if (dias < 60) return 'hace un mes';
  if (dias < 365) return `hace ${mes(dias)} meses`;
  if (dias < 730) return 'hace más de un año';
  return `hace ${Math.floor(dias / 365)} años`;
};

function Prestado() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  // Arranca en el 1 de enero de este año: es el rango que se pide casi siempre
  // —«¿cuánto he prestado este año?»— y ahorra teclear la fecha cada vez.
  const [desde, setDesde] = useState(hoy().slice(0, 4) + '-01-01');
  const [hasta, setHasta] = useState(hoy());
  const [verTodos, setVerTodos] = useState(false);

  const cargar = useCallback(() => {
    axios.get(`/api/prestamos/panorama?desde=${desde}&hasta=${hasta}`)
      .then(r => { setD(r.data || null); setError(''); })
      .catch(() => setError('No se pudo cargar. Recargue la página.'));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!d) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const mayor = Math.max(1, ...d.antiguedad.map(a => a.plata));
  const lista = verTodos ? d.prestamos : d.prestamos.slice(0, 15);

  return (
    <>
      <div className="encabezado">
        <h1>Prestado</h1>
        <p>Lo que les prestó a las fincas y todavía no ha vuelto</p>
      </div>

      {/* ── LA CARTERA, SOLA Y ARRIBA ─────────────────────────────────────
          Una sola cifra, y la que de verdad importa. No lleva fechas a
          propósito: es lo que hay en la calle HOY. */}
      <div className="bloque">
        <div className="cuerpo">
          <div style={{ fontSize: 13, color: 'var(--tinta-tenue)',
                        textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Le deben hoy
          </div>
          <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.1,
                        margin: '4px 0 2px' }}>
            {plata(d.deben_hoy)}
          </div>
          <div style={{ fontSize: 14, color: 'var(--tinta-tenue)' }}>
            en {d.cuantos} {d.cuantos === 1 ? 'préstamo' : 'préstamos'} ·{' '}
            {d.fincas} {d.fincas === 1 ? 'finca' : 'fincas'}
          </div>
          {/* Es el SALDO, no lo prestado. La diferencia no es cosmética: la
              pantalla de Fincas sumaba los montos y con las cuotas eso infla
              la cartera en millones. */}
          <div className="chico" style={{ marginTop: 6 }}>
            Es el saldo: lo prestado menos lo que ya se les descontó.
          </div>
        </div>
      </div>

      {/* ── EL FLUJO: OTRA PREGUNTA ───────────────────────────────────────── */}
      <div className="bloque">
        <h2>📅 Lo que prestó en un tiempo</h2>
        <div className="cuerpo">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap',
                        alignItems: 'flex-end', marginBottom: 14 }}>
            <label className="campo" style={{ margin: 0 }}>
              <span>Desde</span>
              <input type="date" value={desde} max={hasta}
                     onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo" style={{ margin: 0 }}>
              <span>Hasta</span>
              <input type="date" value={hasta} min={desde} max={hoy()}
                     onChange={e => setHasta(e.target.value)} />
            </label>
          </div>

          {d.flujo ? (
            <table className="detalle">
              <tbody>
                <tr>
                  <td>Prestó en ese tiempo</td>
                  <td className="der">{plata(d.flujo.prestado)}</td>
                </tr>
                <tr>
                  <td>Le devolvieron</td>
                  {/* El menos solo cuando hay algo que restar. «−$0» se lee
                      como un error de la máquina. */}
                  <td className="der">
                    {d.flujo.devuelto > 0 ? '−' : ''}{plata(d.flujo.devuelto)}
                  </td>
                </tr>
                <tr className="neto">
                  <td>
                    {d.flujo.prestado >= d.flujo.devuelto
                      ? 'Prestó de más' : 'Recuperó de más'}
                  </td>
                  <td className="der">
                    {plata(Math.abs(d.flujo.prestado - d.flujo.devuelto))}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="vacio">Escoja las dos fechas.</div>
          )}

          {/* Sin esto, las dos cifras de la pantalla se leen como una sola
              cuenta y no cuadran nunca: son cosas distintas. */}
          <div className="chico" style={{ marginTop: 10 }}>
            Esto <strong>no</strong> es lo que le deben: es lo que se movió en
            esas fechas. Un préstamo de marzo ya devuelto cuenta aquí y no
            arriba.
          </div>
        </div>
      </div>

      {/* ── DESDE CUÁNDO LE DEBEN ─────────────────────────────────────────
          Un préstamo de $200.000 de hace catorce meses es peor noticia que
          uno de $3.000.000 de la semana pasada. La antigüedad es lo que
          decide a quién hay que ir a buscar. */}
      <div className="bloque">
        <h2>⏳ Desde cuándo le deben</h2>
        <div className="cuerpo">
          {d.antiguedad.map((a, n) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center',
                                  gap: 12, padding: '7px 0' }}>
              <div style={{ width: 130, fontSize: 14, flexShrink: 0 }}>{a.nombre}</div>
              <div style={{ flex: 1, minWidth: 60, height: 14,
                            background: 'var(--raya)', borderRadius: 3 }}>
                <div style={{
                  width: `${Math.round((a.plata / mayor) * 100)}%`, height: '100%',
                  borderRadius: 3,
                  // Lo viejo se pinta más fuerte: es lo que hay que mirar.
                  background: a.desde >= 365 ? 'var(--malo)'
                            : a.desde >= 180 ? 'var(--ojo)' : 'var(--acento)',
                }} />
              </div>
              <div style={{ width: 120, textAlign: 'right', fontWeight: 600,
                            flexShrink: 0 }}>
                {plata(a.plata)}
              </div>
              <div style={{ width: 80, fontSize: 13, color: 'var(--tinta-tenue)',
                            flexShrink: 0 }}>
                {a.fincas} {a.fincas === 1 ? 'finca' : 'fincas'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── LOS QUE SE LLEVAN LA QUINCENA ENTERA ──────────────────────────
          El bloque que de verdad importa, y no por la plata de Gabriel.

          Un préstamo sin cuota NO se queda atrapado: se descuenta hasta donde
          alcance la leche. El daño es el otro — se come todo lo que entró, y
          esa finca se va sin un peso después de entregar leche quince días.

          Es el aviso que nadie da hasta que el finquero llega por su plata. */}
      {d.se_llevan_todo.length > 0 && (
        <div className="bloque">
          <h2>
            ⚠️ Se van a llevar la quincena entera
            <span className="contador">
              {d.se_llevan_todo.length} ·{' '}
              {plata(d.se_llevan_todo.reduce((s, p) => s + p.saldo, 0))}
            </span>
          </h2>
          <div className="cuerpo">
            <div className="aviso" style={{ marginBottom: 12 }}>
              Estos préstamos <strong>no tienen cuota</strong>, así que se
              descuentan hasta donde alcance la leche. Como lo que deben pasa de
              lo que entregan, <strong>esas fincas van a recibir $0</strong> en
              su próxima liquidación.
              <br />
              Póngales una cuota en 🚜 Fincas y cobran, aunque el préstamo baje
              más despacio.
            </div>
            {d.se_llevan_todo.map(p => (
              <div key={p.id} style={{ padding: '10px 0',
                                       borderTop: '1px solid var(--raya)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap',
                              alignItems: 'baseline' }}>
                  <strong>{p.finca}</strong>
                  <span style={{ fontWeight: 600 }}>{plata(p.saldo)}</span>
                  <span className="pinta malo" style={{ marginLeft: 'auto' }}>
                    sin cuota
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                  Entrega unos {plata(p.entrega_por_periodo)} por liquidación y
                  debe {plata(p.saldo)}: se queda sin nada ·
                  prestado {desdeCuando(p.dias)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── LA LISTA, DEL MÁS VIEJO AL MÁS NUEVO ─────────────────────────── */}
      <div className="bloque">
        <h2>
          💵 Uno por uno
          <span className="contador">{d.prestamos.length}</span>
        </h2>
        <div className="tabla-caja">
          <table className="datos">
            <thead>
              <tr>
                <th>Finca</th>
                <th>Cuándo</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th style={{ textAlign: 'right' }}>Cuota</th>
                <th>Para qué</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(p => (
                <tr key={p.id}>
                  <td className="recio">{p.finca}</td>
                  <td>
                    {fecha(p.fecha)}
                    <div className="chico">{desdeCuando(p.dias)}</div>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {plata(p.saldo)}
                    {/* Solo cuando ya se le abonó algo: si no, repetir el mismo
                        número debajo no dice nada. */}
                    {p.saldo < Math.round(p.monto) && (
                      <div className="chico">de {plata(p.monto)}</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {p.cuota ? (
                      <>
                        {plata(p.cuota)}
                        <div className="chico">
                          {p.faltan} {p.faltan === 1 ? 'vez' : 'veces'}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: 'var(--tinta-tenue)' }}>entero</span>
                    )}
                  </td>
                  <td className="chico">{p.nota || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.prestamos.length > 15 && (
          <div className="cuerpo">
            <button className="boton suave" onClick={() => setVerTodos(!verTodos)}>
              {verTodos ? 'Ver solo los 15 más viejos'
                        : `Ver los ${d.prestamos.length} completos`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default Prestado;
